/**
 * canvasSchema — v3 envelope 프론트 측 유틸 (backend services/canvas_schema.py와 paired)
 *
 * format_version: 'whatquiz_presentation_v3'  (v2 문서도 그대로 읽힌다)
 * 슬라이드 좌표계: 1280x720 (HtmlSlideRenderer와 동일), 학습지는 A4 794x1123
 *
 * v3에서 추가된 것 (모두 additive — v2만 아는 코드도 v3 문서를 깨지 않는다)
 * - 객체 공통 필드: groupId(string|null), origin('ai'|'user', 기본 'ai'), locked(boolean)
 *   · locked는 semantic.locked가 정본이다. top-level `locked`는 미러(편의 필드).
 *     양방향 정규화는 isObjectLocked / setObjectLocked / normalizeObjectV3 참조.
 * - 객체 타입 'table': { rows, cols, cells[rows][cols], border, cellPadding, header,
 *   semantic.role 'table'|'answer-grid' (+ gridRows/gridCols) }
 *   · N×N 답란 격자는 role 'answer-grid'인 table 하나로 표현한다.
 *   · 렌더링은 WP-F에서 담당. 여기서는 스키마·검증·마이그레이션만.
 * - canvas.groups (선택): [{ id, label?, locked? }]. 선언하지 않아도 groupId 공유로 암묵 그룹이 성립.
 *
 * 하위 호환 규칙
 * - isV2Envelope(outline)는 v2·v3 모두 true (기존 호출부가 재요청하지 않도록).
 * - stampFormatVersion은 항상 v3를 찍는다.
 * - 쓰기 시 v3 필드는 기본값이 아닐 때만 기록하고(sparse), 읽기 시 normalize로 채운다.
 *
 * 백엔드와 동일한 객체 타입/필드를 사용.
 * 변환은 서버 endpoint(/api/ppt/outline-to-canvas)를 우선 사용하고,
 * 클라이언트 fallback이 필요할 때만 lazy populate.
 */

import { koreanOrNull } from './koreanMessage';

export const CANVAS_FORMAT_VERSION_V2 = 'whatquiz_presentation_v2';
export const CANVAS_FORMAT_VERSION_V3 = 'whatquiz_presentation_v3';
export const CANVAS_FORMAT_VERSION = CANVAS_FORMAT_VERSION_V3;
export const SUPPORTED_CANVAS_FORMAT_VERSIONS = new Set([
  CANVAS_FORMAT_VERSION_V2,
  CANVAS_FORMAT_VERSION_V3,
]);

export const CANVAS_WIDTH = 1280;
export const CANVAS_HEIGHT = 720;

export const VALID_OBJECT_TYPES = new Set(['textbox', 'shape', 'image', 'icon', 'chart', 'table']);
export const VALID_SHAPE_KINDS = new Set(['rect', 'circle', 'triangle', 'line', 'ellipse']);

export const OBJECT_ORIGINS = new Set(['ai', 'user']);
export const DEFAULT_OBJECT_ORIGIN = 'ai';

export const TABLE_ROLES = new Set(['table', 'answer-grid']);
export const TABLE_CELL_ALIGNS = new Set(['left', 'center', 'right']);
export const DEFAULT_TABLE_BORDER = Object.freeze({ color: '#8B95A1', width: 1 });
export const DEFAULT_TABLE_CELL_PADDING = 6;
export const MAX_TABLE_ROWS = 60;
export const MAX_TABLE_COLS = 40;

/**
 * 캔버스가 선언한 실제 지면 크기를 반환한다.
 *
 * PPT는 1280x720을 기본값으로 쓰지만 학습지는 A4 794x1123처럼 다른 좌표계를
 * 사용한다. 편집기·썸네일·내보내기가 같은 좌표계를 보도록 이 함수를 공통으로 쓴다.
 */
export function getCanvasDimensions(slideOrCanvas, fallback = {}) {
  const canvas = slideOrCanvas?.canvas || slideOrCanvas || {};
  const rawWidth = Number(canvas?.width);
  const rawHeight = Number(canvas?.height);
  const fallbackWidth = Number(fallback?.width);
  const fallbackHeight = Number(fallback?.height);

  return {
    width: Number.isFinite(rawWidth) && rawWidth > 0
      ? rawWidth
      : (Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : CANVAS_WIDTH),
    height: Number.isFinite(rawHeight) && rawHeight > 0
      ? rawHeight
      : (Number.isFinite(fallbackHeight) && fallbackHeight > 0 ? fallbackHeight : CANVAS_HEIGHT),
  };
}

export function getFormatVersion(outline) {
  if (!outline || typeof outline !== 'object') return null;
  const v = outline.format_version;
  return typeof v === 'string' ? v : null;
}

/** v2 또는 v3 캔버스 envelope인지 (읽기 호환 판정). */
export function isCanvasEnvelope(outline) {
  const v = getFormatVersion(outline);
  return v != null && SUPPORTED_CANVAS_FORMAT_VERSIONS.has(v);
}

/**
 * 하위 호환 alias — "캔버스가 들어있는 envelope인가"의 의미로 쓰이던 함수.
 * v3 문서도 true를 돌려주어 기존 호출부(ensureCanvasForOutline 등)가 v3 문서를
 * 다시 서버로 보내지 않게 한다.
 */
export function isV2Envelope(outline) {
  return isCanvasEnvelope(outline);
}

export function isV3Envelope(outline) {
  return getFormatVersion(outline) === CANVAS_FORMAT_VERSION_V3;
}

export function hasCanvas(slide) {
  return !!slide && typeof slide === 'object'
    && slide.canvas && typeof slide.canvas === 'object'
    && Array.isArray(slide.canvas.objects);
}

/** 항상 현재 버전(v3)을 찍는다. 불변 — 새 객체를 돌려준다. */
export function stampFormatVersion(outline) {
  if (!outline || typeof outline !== 'object') return outline;
  if (outline.format_version !== CANVAS_FORMAT_VERSION) {
    return { ...outline, format_version: CANVAS_FORMAT_VERSION };
  }
  return outline;
}

// ---------------------------------------------------------------------------
// 객체 공통 v3 필드: locked / origin / groupId
// ---------------------------------------------------------------------------

/** semantic.locked가 정본. 없으면 top-level locked. */
export function isObjectLocked(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const sem = obj.semantic;
  if (sem && typeof sem === 'object' && 'locked' in sem) return !!sem.locked;
  return !!obj.locked;
}

/** semantic.locked와 top-level locked를 함께 갱신한 새 객체를 돌려준다 (불변). */
export function setObjectLocked(obj, locked) {
  const flag = !!locked;
  const sem = obj?.semantic && typeof obj.semantic === 'object' ? obj.semantic : {};
  return { ...obj, locked: flag, semantic: { ...sem, locked: flag } };
}

export function getObjectOrigin(obj) {
  const origin = obj && obj.origin;
  return OBJECT_ORIGINS.has(origin) ? origin : DEFAULT_OBJECT_ORIGIN;
}

export function getObjectGroupId(obj) {
  const gid = obj && obj.groupId;
  return typeof gid === 'string' && gid ? gid : null;
}

/**
 * 사용자가 추가한 객체인지.
 * origin이 있으면 그것을 따르고(v3), 없으면 v2 규칙(sourceField·generated 없음)으로 추론한다.
 * backend outline_to_canvas.repopulate_canvas_preserving_locked와 같은 규칙.
 */
export function isUserObject(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (OBJECT_ORIGINS.has(obj.origin)) return obj.origin === 'user';
  const sem = obj.semantic && typeof obj.semantic === 'object' ? obj.semantic : {};
  return !sem.sourceField && !sem.generated;
}

/**
 * v3 기본값을 명시적으로 채운 새 객체 (불변, 멱등).
 * - groupId: 없으면 null
 * - origin: 없으면 추론(사용자 객체면 'user', 아니면 'ai')
 * - locked: semantic.locked 정본으로 양쪽 동기화
 * - table: border/cellPadding/header/cells 기본값
 */
export function normalizeObjectV3(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const sem = obj.semantic && typeof obj.semantic === 'object' ? { ...obj.semantic } : {};
  const locked = isObjectLocked(obj);
  sem.locked = locked;
  const next = {
    ...obj,
    groupId: getObjectGroupId(obj),
    origin: OBJECT_ORIGINS.has(obj.origin) ? obj.origin : (isUserObject(obj) ? 'user' : 'ai'),
    locked,
    semantic: sem,
  };
  if (next.type === 'table') return normalizeTableDefaults(next);
  return next;
}

function normalizeTableDefaults(table) {
  const rows = Math.max(1, Number(table.rows) || 1);
  const cols = Math.max(1, Number(table.cols) || 1);
  const next = { ...table, rows, cols };
  if (!next.border || typeof next.border !== 'object') next.border = { ...DEFAULT_TABLE_BORDER };
  if (typeof next.cellPadding !== 'number') next.cellPadding = DEFAULT_TABLE_CELL_PADDING;
  if (typeof next.header !== 'boolean') next.header = !!next.header;
  if (!Array.isArray(next.cells)) next.cells = makeEmptyTableCells(rows, cols);
  const sem = { ...(next.semantic || {}) };
  if (!TABLE_ROLES.has(sem.role)) sem.role = 'table';
  if (sem.role === 'answer-grid') {
    if (typeof sem.gridRows !== 'number') sem.gridRows = rows;
    if (typeof sem.gridCols !== 'number') sem.gridCols = cols;
  }
  next.semantic = sem;
  return next;
}

/** canvas 안의 모든 객체를 normalizeObjectV3로 정규화한 새 canvas. */
export function normalizeCanvasV3(canvas) {
  if (!canvas || typeof canvas !== 'object' || !Array.isArray(canvas.objects)) return canvas;
  return { ...canvas, objects: canvas.objects.map(normalizeObjectV3) };
}

/**
 * v2 → v3 마이그레이션 (불변, 멱등).
 * - outline(slides[].canvas) 또는 단일 canvas 모두 받는다.
 * - outline이면 format_version을 v3로 찍는다. 다른 필드는 그대로.
 */
export function upgradeCanvasV2ToV3(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  if (Array.isArray(doc.slides)) {
    const slides = doc.slides.map((slide) => (
      hasCanvas(slide) ? { ...slide, canvas: normalizeCanvasV3(slide.canvas) } : slide
    ));
    return stampFormatVersion({ ...doc, slides });
  }
  if (Array.isArray(doc.objects)) return normalizeCanvasV3(doc);
  return doc;
}

// ---------------------------------------------------------------------------
// 객체 생성
// ---------------------------------------------------------------------------

/**
 * opts: rotate, z, html, text, src, shape, style, semantic,
 *       groupId(string), origin('ai'|'user'), locked(boolean)
 * v3 필드는 기본값이 아닐 때만 기록한다 (sparse write).
 */
export function makeObject(id, type, x, y, w, h, opts = {}) {
  const obj = {
    id,
    type,
    x: Number(x),
    y: Number(y),
    w: Number(w),
    h: Number(h),
    rotate: Number(opts.rotate ?? 0),
    z: opts.z ?? 0,
  };
  if (opts.html != null) obj.html = opts.html;
  if (opts.text != null) obj.text = opts.text;
  if (opts.src != null) obj.src = opts.src;
  if (opts.shape != null) obj.shape = opts.shape;
  if (opts.style) obj.style = { ...opts.style };
  if (opts.semantic) obj.semantic = { ...opts.semantic };
  if (opts.groupId != null) obj.groupId = String(opts.groupId);
  if (opts.origin != null) {
    if (!OBJECT_ORIGINS.has(opts.origin)) throw new Error(`origin invalid: ${opts.origin}`);
    if (opts.origin !== DEFAULT_OBJECT_ORIGIN) obj.origin = opts.origin;
  }
  if (opts.locked) {
    obj.locked = true;
    obj.semantic = { ...(obj.semantic || {}), locked: true };
  }
  return obj;
}

export function makeTableCell(text = '', opts = {}) {
  const cell = { text: text == null ? '' : String(text) };
  if (opts.html != null) cell.html = opts.html;
  if (opts.colspan != null && Number(opts.colspan) !== 1) cell.colspan = Number(opts.colspan);
  if (opts.rowspan != null && Number(opts.rowspan) !== 1) cell.rowspan = Number(opts.rowspan);
  if (opts.align != null) cell.align = opts.align;
  if (opts.fill != null) cell.fill = opts.fill;
  return cell;
}

/** 병합으로 덮인 자리 표시 셀. */
export function makeCoveredCell() {
  return { text: '', covered: true };
}

export function makeEmptyTableCells(rows, cols) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => makeTableCell('')));
}

/**
 * table 객체 생성.
 * opts: rows(필수), cols(필수), cells, border, cellPadding, header, role('table'|'answer-grid'),
 *       semantic, style, z, groupId, origin, locked
 * role 'answer-grid'면 semantic.gridRows/gridCols를 rows/cols로 채운다.
 */
export function makeTableObject(id, x, y, w, h, opts = {}) {
  const rows = Number(opts.rows);
  const cols = Number(opts.cols);
  if (!Number.isInteger(rows) || rows < 1 || rows > MAX_TABLE_ROWS) {
    throw new Error(`table rows must be 1..${MAX_TABLE_ROWS}, got ${opts.rows}`);
  }
  if (!Number.isInteger(cols) || cols < 1 || cols > MAX_TABLE_COLS) {
    throw new Error(`table cols must be 1..${MAX_TABLE_COLS}, got ${opts.cols}`);
  }
  const role = opts.role ?? 'table';
  if (!TABLE_ROLES.has(role)) throw new Error(`table role invalid: ${role}`);

  const semantic = { ...(opts.semantic || {}), role };
  if (role === 'answer-grid') {
    semantic.gridRows = rows;
    semantic.gridCols = cols;
  }
  const obj = makeObject(id, 'table', x, y, w, h, {
    z: opts.z ?? 1,
    style: opts.style,
    semantic,
    groupId: opts.groupId,
    origin: opts.origin,
    locked: opts.locked,
  });
  obj.rows = rows;
  obj.cols = cols;
  obj.cells = Array.isArray(opts.cells) ? opts.cells : makeEmptyTableCells(rows, cols);
  obj.border = opts.border ? { ...opts.border } : { ...DEFAULT_TABLE_BORDER };
  obj.cellPadding = opts.cellPadding ?? DEFAULT_TABLE_CELL_PADDING;
  obj.header = !!opts.header;

  const errors = validateTableObject(obj);
  if (errors.length) throw new Error(`invalid table: ${errors.join('; ')}`);
  return obj;
}

export function makeCanvas(objects, opts = {}) {
  const canvas = {
    width: opts.width ?? CANVAS_WIDTH,
    height: opts.height ?? CANVAS_HEIGHT,
    background: opts.background ?? '#FFFFFF',
    objects: Array.isArray(objects) ? objects : [],
  };
  if (Array.isArray(opts.groups)) canvas.groups = opts.groups.map((g) => ({ ...g }));
  return canvas;
}

// ---------------------------------------------------------------------------
// 검증
// ---------------------------------------------------------------------------

/**
 * table 객체 검증 — 오류 메시지 배열 (backend validate_table_object와 같은 규칙).
 * - cells는 정확히 rows×cols 행렬
 * - colspan/rowspan은 격자 밖으로 나갈 수 없음
 * - answer-grid는 semantic.gridRows/gridCols가 rows/cols와 일치
 */
export function validateTableObject(obj) {
  const errors = [];
  if (!obj || typeof obj !== 'object') return ['table must be object'];
  const rows = obj.rows;
  const cols = obj.cols;
  if (!Number.isInteger(rows) || rows < 1 || rows > MAX_TABLE_ROWS) {
    errors.push(`table.rows must be int 1..${MAX_TABLE_ROWS}`);
  }
  if (!Number.isInteger(cols) || cols < 1 || cols > MAX_TABLE_COLS) {
    errors.push(`table.cols must be int 1..${MAX_TABLE_COLS}`);
  }
  if (errors.length) return errors;

  const cells = obj.cells;
  if (!Array.isArray(cells)) {
    errors.push('table.cells must be array');
    return errors;
  }
  if (cells.length !== rows) errors.push(`table.cells has ${cells.length} rows, expected ${rows}`);
  cells.forEach((row, r) => {
    if (!Array.isArray(row)) {
      errors.push(`table.cells[${r}] must be array`);
      return;
    }
    if (row.length !== cols) errors.push(`table.cells[${r}] has ${row.length} cells, expected ${cols}`);
    row.forEach((cell, c) => {
      if (!cell || typeof cell !== 'object') {
        errors.push(`table.cells[${r}][${c}] must be object`);
        return;
      }
      if (cell.text != null && typeof cell.text !== 'string') {
        errors.push(`table.cells[${r}][${c}].text must be string`);
      }
      if (cell.html != null && typeof cell.html !== 'string') {
        errors.push(`table.cells[${r}][${c}].html must be string`);
      }
      const colspan = cell.colspan ?? 1;
      const rowspan = cell.rowspan ?? 1;
      if (!Number.isInteger(colspan) || colspan < 1) {
        errors.push(`table.cells[${r}][${c}].colspan must be int >= 1`);
      } else if (c + colspan > cols) {
        errors.push(`table.cells[${r}][${c}].colspan ${colspan} exceeds cols`);
      }
      if (!Number.isInteger(rowspan) || rowspan < 1) {
        errors.push(`table.cells[${r}][${c}].rowspan must be int >= 1`);
      } else if (r + rowspan > rows) {
        errors.push(`table.cells[${r}][${c}].rowspan ${rowspan} exceeds rows`);
      }
      if (cell.align != null && !TABLE_CELL_ALIGNS.has(cell.align)) {
        errors.push(`table.cells[${r}][${c}].align invalid: ${cell.align}`);
      }
    });
  });

  if (obj.border != null) {
    if (typeof obj.border !== 'object') errors.push('table.border must be object');
    else if (obj.border.width != null && typeof obj.border.width !== 'number') {
      errors.push('table.border.width must be number');
    }
  }
  if (obj.cellPadding != null && typeof obj.cellPadding !== 'number') {
    errors.push('table.cellPadding must be number');
  }
  if (obj.header != null && typeof obj.header !== 'boolean') errors.push('table.header must be boolean');

  const sem = obj.semantic && typeof obj.semantic === 'object' ? obj.semantic : {};
  const role = sem.role;
  if (role != null && !TABLE_ROLES.has(role)) errors.push(`table.semantic.role invalid: ${role}`);
  if (role === 'answer-grid') {
    if (sem.gridRows != null && sem.gridRows !== rows) {
      errors.push(`table.semantic.gridRows ${sem.gridRows} != rows ${rows}`);
    }
    if (sem.gridCols != null && sem.gridCols !== cols) {
      errors.push(`table.semantic.gridCols ${sem.gridCols} != cols ${cols}`);
    }
  }
  return errors;
}

export function validateCanvas(canvas) {
  const errors = [];
  if (!canvas || typeof canvas !== 'object') {
    errors.push('canvas must be object');
    return errors;
  }
  if (!Array.isArray(canvas.objects)) {
    errors.push('canvas.objects must be array');
    return errors;
  }
  let declaredGroups = null;
  if (Array.isArray(canvas.groups)) {
    declaredGroups = new Set();
    canvas.groups.forEach((g, gi) => {
      if (!g || typeof g !== 'object' || !g.id) errors.push(`groups[${gi}].id missing`);
      else declaredGroups.add(String(g.id));
    });
  }
  canvas.objects.forEach((obj, i) => {
    if (!obj || typeof obj !== 'object') {
      errors.push(`objects[${i}] must be object`);
      return;
    }
    if (!obj.id) errors.push(`objects[${i}].id missing`);
    if (!VALID_OBJECT_TYPES.has(obj.type)) errors.push(`objects[${i}].type invalid: ${obj.type}`);
    ['x', 'y', 'w', 'h'].forEach((k) => {
      if (typeof obj[k] !== 'number') errors.push(`objects[${i}].${k} not number`);
    });
    if (obj.type === 'shape' && !VALID_SHAPE_KINDS.has(obj.shape)) {
      errors.push(`objects[${i}].shape invalid: ${obj.shape}`);
    }
    if (obj.type === 'table') {
      validateTableObject(obj).forEach((e) => errors.push(`objects[${i}].${e}`));
    }
    // v3 공통 필드 (없으면 v2 객체 — 통과)
    if ('groupId' in obj && obj.groupId != null) {
      if (typeof obj.groupId !== 'string') errors.push(`objects[${i}].groupId must be str or null`);
      else if (declaredGroups && !declaredGroups.has(obj.groupId)) {
        errors.push(`objects[${i}].groupId references unknown group: ${obj.groupId}`);
      }
    }
    if ('origin' in obj && !OBJECT_ORIGINS.has(obj.origin)) {
      errors.push(`objects[${i}].origin invalid: ${obj.origin}`);
    }
    if ('locked' in obj && typeof obj.locked !== 'boolean') {
      errors.push(`objects[${i}].locked must be bool`);
    }
  });
  return errors;
}

export function collectSemanticObjects(slide, role = null) {
  if (!hasCanvas(slide)) return [];
  return slide.canvas.objects.filter((obj) => {
    const sem = obj && obj.semantic;
    if (!sem || typeof sem !== 'object') return false;
    if (role == null) return true;
    return sem.role === role;
  });
}

/** 같은 groupId를 가진 객체 (z 순서 유지). */
export function collectGroupObjects(canvas, groupId) {
  if (!groupId || !canvas || !Array.isArray(canvas.objects)) return [];
  return canvas.objects.filter((obj) => getObjectGroupId(obj) === groupId);
}

/** { groupId: 객체 수 } */
export function collectGroupIds(canvas) {
  const counts = {};
  if (!canvas || !Array.isArray(canvas.objects)) return counts;
  canvas.objects.forEach((obj) => {
    const gid = getObjectGroupId(obj);
    if (gid) counts[gid] = (counts[gid] || 0) + 1;
  });
  return counts;
}

/**
 * 학습지 outline → A4 캔버스 페이지.
 *
 * 실패하면 { success: false } 를 돌려주고, 호출부는 기존 이미지 학습지 흐름을 그대로 쓸 수 있다.
 */
export async function buildWorksheetCanvas({
  outline,
  theme,
  paperSize,
  orientation,
  includeAnswerGuide = false,
  worksheetStyle,
  signal,
}) {
  if (!outline || typeof outline !== 'object') {
    return { success: false, message: '학습지 내용을 찾을 수 없어요.' };
  }
  try {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${API_BASE}/api/worksheet/outline-to-canvas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        outline,
        theme,
        paper_size: paperSize || 'A4',
        orientation: orientation || 'portrait',
        include_answer_guide: includeAnswerGuide,
        worksheet_style: worksheetStyle || undefined,
      }),
      signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    if (e?.name === 'AbortError') return { success: false, aborted: true };
    console.warn('[canvasSchema] buildWorksheetCanvas 실패:', e?.message);
    return { success: false, message: '학습지 미리보기를 만들지 못했어요.' };
  }
}

/**
 * 학습지 A4 캔버스 → 텍스트가 살아 있는 PDF.
 */
export async function downloadWorksheetCanvasPdf({ outline, filename, signal }) {
  try {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${API_BASE}/api/worksheet/canvas-to-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outline, filename }),
      signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const raw = typeof body?.detail === 'string'
        ? body.detail
        : (typeof body?.detail?.message === 'string'
          ? body.detail.message
          : (typeof body?.message === 'string' ? body.message : null));
      return {
        success: false,
        message: koreanOrNull(raw) || 'PDF를 만들지 못했어요. 잠시 후 다시 시도해 주세요',
      };
    }
    return await res.json();
  } catch (e) {
    if (e?.name === 'AbortError') return { success: false, aborted: true };
    console.warn('[canvasSchema] downloadWorksheetCanvasPdf 실패:', e?.message);
    return {
      success: false,
      message: koreanOrNull(e?.message) || 'PDF를 만들지 못했어요. 잠시 후 다시 시도해 주세요',
    };
  }
}

/**
 * 백엔드 outline_to_canvas endpoint 호출.
 * 응답이 실패하면 outline 그대로 반환 (graceful fallback).
 */
export async function ensureCanvasForOutline(outline, options = {}) {
  if (!outline || typeof outline !== 'object') return outline;
  if (isV2Envelope(outline) && Array.isArray(outline.slides) && outline.slides.every(hasCanvas)) {
    return outline;
  }
  try {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${API_BASE}/api/ppt/outline-to-canvas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outline }),
      signal: options.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data?.outline ? stampFormatVersion(data.outline) : outline;
  } catch (e) {
    if (options.silent !== false) {
      console.warn('[canvasSchema] ensureCanvasForOutline 실패, outline 그대로 반환:', e?.message);
    }
    return outline;
  }
}
